import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api, ApiError, type PlayerHistoricalResultsPayload, type PlayerProfileMatch } from "../lib/api";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { makeLatestOnly } from "../lib/latestOnly";
import { matchScoreFromWinner } from "../lib/scoreFromWinner";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import { formatDateWithTime } from "../lib/dateFormat";

const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function resultLabel(result: PlayerProfileMatch["result"]) {
  if (result === "win") return "Ganado";
  if (result === "loss") return "Perdido";
  if (result === "draw") return "Empate";
  if (result === "pending") return "Pendiente";
  if (result === "double_walkover") return "Doble W.O.";
  return "—";
}

function matchTypeLabel(type: string | null | undefined) {
  if (type === "final") return "Final";
  if (type === "playoff") return "Playoffs";
  return "Normal";
}

function dateYear(date: string | null) {
  if (!date || date.length < 4) return "";
  return date.slice(0, 4);
}

function dateMonth(date: string | null) {
  if (!date || date.length < 7) return "";
  return date.slice(5, 7);
}

function dateSortValue(date: string | null) {
  if (!date) return -1;
  const parsed = Date.parse(date);
  return Number.isNaN(parsed) ? -1 : parsed;
}

function roundSortValue(match: PlayerProfileMatch) {
  const text = [match.round_label, match.match_number].filter(Boolean).join(" ");
  const found = text.match(/\d+/);
  return found ? Number(found[0]) : -1;
}

function compareHistoricalRows(a: PlayerProfileMatch, b: PlayerProfileMatch) {
  const roundDelta = roundSortValue(b) - roundSortValue(a);
  if (roundDelta !== 0) return roundDelta;
  const dateDelta = dateSortValue(b.scheduled_date) - dateSortValue(a.scheduled_date);
  if (dateDelta !== 0) return dateDelta;
  const timeDelta = (b.scheduled_time ?? "").localeCompare(a.scheduled_time ?? "");
  if (timeDelta !== 0) return timeDelta;
  return b.id - a.id;
}

export default function PlayerHistoricalResultsPage() {
  const { token } = useAuth();
  // Cuenta familiar: los resultados son del perfil activo y aquí también se
  // puede cambiar de hijo (Eduardo 2026-09-29).
  const scope = usePlayerProfileScope();
  const activeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);
  const [data, setData] = useState<PlayerHistoricalResultsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tournamentFilter, setTournamentFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(async () => {
    if (!token || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const response = await api.player.historicalResults(token, activeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      setData(response.data);
      setTournamentFilter("all");
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar tus resultados históricos.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [activeProfileId, latestLoad, scope.ready, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredResults = useMemo(() => {
    const rows = data?.results ?? [];
    const filtered = rows.filter((match) => {
      if (tournamentFilter !== "all" && String(match.tournament_id) !== tournamentFilter) return false;
      if (yearFilter !== "all" && dateYear(match.scheduled_date) !== yearFilter) return false;
      if (monthFilter !== "all" && dateMonth(match.scheduled_date) !== monthFilter) return false;
      if (typeFilter !== "all" && (match.match_type || "normal") !== typeFilter) return false;
      return match.result !== "pending";
    });
    const tournamentIds = new Set(filtered.map((match) => match.tournament_id));
    if (tournamentIds.size === 1) {
      return [...filtered].sort(compareHistoricalRows);
    }
    return filtered;
  }, [data, monthFilter, tournamentFilter, typeFilter, yearFilter]);

  const years = useMemo(() => {
    const fromApi = data?.filters.years ?? [];
    if (fromApi.length > 0) return fromApi;
    return Array.from(new Set((data?.results ?? []).map((m) => dateYear(m.scheduled_date)).filter(Boolean))).sort().reverse();
  }, [data]);

  const months = useMemo(() => {
    const fromApi = data?.filters.months ?? [];
    if (fromApi.length > 0) return fromApi;
    return Array.from(new Set((data?.results ?? []).map((m) => Number(dateMonth(m.scheduled_date))).filter(Boolean))).sort((a, b) => a - b);
  }, [data]);

  const totals = useMemo(() => {
    return filteredResults.reduce(
      (acc, match) => {
        acc.total += 1;
        if (match.result === "win") acc.wins += 1;
        if (match.result === "loss") acc.losses += 1;
        if (match.result === "draw") acc.draws += 1;
        return acc;
      },
      { total: 0, wins: 0, losses: 0, draws: 0 },
    );
  }, [filteredResults]);

  if (loading && !data) {
    return <section className="card"><p className="muted">Cargando resultados históricos…</p></section>;
  }

  if (error) {
    return <section className="card"><p className="form-error" role="alert">{error}</p></section>;
  }

  return (
    <section className="card player-historical-results">
      <div className="card__header-row">
        <div>
          <h1 className="card__title">Draw histórico</h1>
          <p className="card__subtitle">
            Todos los partidos registrados de {data?.profile.display_name ?? "tu perfil"}, con filtros por torneo, fecha y tipo de juego.
          </p>
        </div>
        <Link className="btn btn--ghost btn--compact" to="/player/profile">Volver a Mi Perfil</Link>
      </div>
      <PlayerProfileScopeSwitcher scope={scope} />

      <div className="historical-results-summary" aria-label="Resumen filtrado">
        <div><span>Total</span><strong>{totals.total}</strong></div>
        <div><span>Ganados</span><strong>{totals.wins}</strong></div>
        <div><span>Perdidos</span><strong>{totals.losses}</strong></div>
        <div><span>Empates</span><strong>{totals.draws}</strong></div>
      </div>

      <div className="historical-results-filters">
        <label>
          <span>Torneo</span>
          <select value={tournamentFilter} onChange={(event) => setTournamentFilter(event.target.value)}>
            <option value="all">Todos</option>
            {(data?.filters.tournaments ?? []).map((tournament) => (
              <option key={tournament.id} value={tournament.id}>{tournament.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Mes</span>
          <select value={monthFilter} onChange={(event) => setMonthFilter(event.target.value)}>
            <option value="all">Todos</option>
            {months.map((month) => (
              <option key={month} value={String(month).padStart(2, "0")}>{MONTHS[month - 1] ?? month}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Año</span>
          <select value={yearFilter} onChange={(event) => setYearFilter(event.target.value)}>
            <option value="all">Todos</option>
            {years.map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
        </label>
        <label>
          <span>Tipo de juego</span>
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            <option value="all">Todos</option>
            <option value="normal">Normal</option>
            <option value="playoff">Playoffs</option>
            <option value="final">Final</option>
          </select>
        </label>
      </div>

      {filteredResults.length === 0 ? (
        <div className="player-profile-empty-state">
          <strong>No hay resultados con esos filtros.</strong>
          <span>Cambia torneo, mes, año o tipo de juego para ampliar la búsqueda.</span>
        </div>
      ) : (
        <div className="responsive-table-wrap">
          <table className="data-table dashboard-table historical-results-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Torneo</th>
                <th>Rival</th>
                <th>Resultado</th>
                <th>Score</th>
                <th>Tipo</th>
                <th>Ronda</th>
                <th>Grupo</th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.map((match) => (
                <tr key={match.id}>
                  <td>{formatDateWithTime(match.scheduled_date, match.scheduled_time)}</td>
                  <td className="data-table__cell--name">{match.tournament_name ?? "—"}</td>
                  <td>{match.status === "bye" ? "Descanso (BYE)" : (match.opponent?.display_name ?? "Rival por definir")}</td>
                  <td><span className={`player-result-card__badge player-result-card__badge--${match.result}`}>{match.status === "bye" ? "BYE" : resultLabel(match.result)}</span></td>
                  <td>
                    {/* Eduardo 2026-10-06: el marcador se muestra desde quien ganó. */}
                    <strong>
                      {matchScoreFromWinner({
                        score_text: match.score_text,
                        winner_participant_id: match.winner_participant_id,
                        player1_participant_id: match.player1?.participant_id ?? null,
                        player2_participant_id: match.player2?.participant_id ?? null,
                      }).trim() || "—"}
                    </strong>
                  </td>
                  <td>{matchTypeLabel(match.match_type)}</td>
                  <td>{match.round_display_label ?? match.round_label ?? `Partido ${match.match_number ?? ""}`}</td>
                  <td>{match.group_name ?? match.division_name ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
